import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const { login, verifyLogin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });
  const otpFieldRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      const dest = location.state?.from || '/orders';
      navigate(dest, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  const showError = (message) => setSnack({ open: true, severity: 'error', message });
  const showSuccess = (message) => setSnack({ open: true, severity: 'success', message });

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    const phone = phoneNumber.trim();
    if (!/^\d{10}$/.test(phone)) {
      showError('Please enter a valid 10-digit phone number');
      return;
    }
    setIsLoading(true);
    try {
      const response = await login(phone);
      if (response.success) {
        setIsOtpSent(true);
        showSuccess(response.message || 'OTP sent successfully');
        setTimeout(() => otpFieldRef.current?.focus(), 100);
      } else {
        showError(response.message || 'Failed to send OTP');
      }
    } catch (err) {
      showError(err?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (otpCode) => {
    const code = (otpCode ?? otp).trim();
    if (!/^\d{6}$/.test(code)) {
      showError('Please enter the 6-digit OTP');
      return;
    }
    setIsLoading(true);
    try {
      const response = await verifyLogin(phoneNumber, code);
      if (response.success) {
        showSuccess('Login successful');
      } else {
        showError(response.message || 'Invalid OTP');
      }
    } catch (err) {
      showError(err?.message || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = () => {
    setIsOtpSent(false);
    setOtp('');
  };

  const handleOtpChange = (raw) => {
    const digits = (raw || '').replace(/\D/g, '').slice(0, 6);
    setOtp(digits);
    if (digits.length === 6) {
      setTimeout(() => handleVerify(digits), 250);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          height: 240,
          bgcolor: 'primary.main',
          borderBottomLeftRadius: 24,
          borderBottomRightRadius: 24,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          px: 2,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
          }}
        >
          <RestaurantIcon sx={{ fontSize: 36, color: '#fff' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Restaurant Dashboard
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: '#F3E6EA' }}>
          Manage your restaurant orders efficiently
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ mt: -3 }}>
        <Card elevation={4} sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: 3 }}>
            {!isOtpSent ? (
              <Box component="form" onSubmit={handleSendOtp}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Login
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter your phone number to receive an OTP
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  inputProps={{ inputMode: 'tel', maxLength: 10 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                  }}
                  sx={{ mb: 2 }}
                  autoFocus
                  autoComplete="tel-national"
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{ height: 52 }}
                >
                  {isLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Send OTP'}
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Verify OTP
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter the 6-digit code sent to your phone
                </Typography>
                <TextField
                  inputRef={otpFieldRef}
                  fullWidth
                  placeholder="OTP"
                  value={otp}
                  onChange={(e) => handleOtpChange(e.target.value)}
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: 6,
                    style: { letterSpacing: 6, textAlign: 'center', fontSize: 18, fontWeight: 600 },
                  }}
                  autoFocus
                  autoComplete="one-time-code"
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{ height: 52, mb: 1 }}
                  onClick={() => handleVerify()}
                >
                  {isLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Verify OTP'}
                </Button>
                <Button fullWidth onClick={handleResendOtp} sx={{ color: 'primary.main' }}>
                  Resend OTP
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', mt: 3, color: 'text.secondary' }}
        >
          By logging in, you agree to our Terms of Service and Privacy Policy
        </Typography>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
